import type { ReactElement } from 'react';
import { Badge, Button, Group, Text } from '@mantine/core';
import { Layers } from 'lucide-react';
import type { MegaGridGroupBy, MegaGridGroupByPill } from './MegaGridTypes';

interface MegaGridGroupByPillsProps {
    pills: MegaGridGroupByPill[];
    groupBy: MegaGridGroupBy[];
    onAdd: (columnKey: string) => void;
    onRemove: (columnKey: string) => void;
    onClear: () => void;
}

/**
 * Flat, single-row group-by control: each supplied pill toggles a grouping
 * level, showing its position in the group order as an ordinal badge. Opt-in
 * alternative to {@link MegaGridGroupByEditor}; the host supplies the definitive
 * pill list.
 */
export default function MegaGridGroupByPills({ pills, groupBy, onAdd, onRemove, onClear }: MegaGridGroupByPillsProps): ReactElement {
    const orderOf = new Map(groupBy.map((g, i) => [g.columnKey, i + 1]));

    return (
        <Group gap={6} wrap="wrap" align="center">
            <Layers size={14} style={{ color: 'var(--bm-color-text-dimmed)' }} />
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mr={2}>
                Group by
            </Text>
            {pills.map((pill) => {
                const ordinal = orderOf.get(pill.columnKey);
                const active = ordinal !== undefined;
                return (
                    <Button
                        key={pill.columnKey}
                        size="compact-xs"
                        radius="xl"
                        variant={active ? 'filled' : 'default'}
                        color={active ? 'brand' : undefined}
                        onClick={() => (active ? onRemove(pill.columnKey) : onAdd(pill.columnKey))}
                        rightSection={
                            active ? (
                                <Badge size="xs" circle variant="white" color="brand">
                                    {ordinal}
                                </Badge>
                            ) : undefined
                        }
                    >
                        {pill.label}
                    </Button>
                );
            })}
            {groupBy.length > 0 && (
                <Button size="compact-xs" variant="subtle" color="gray" onClick={onClear}>
                    Clear
                </Button>
            )}
        </Group>
    );
}
